/**
 * review_clause — safe agent tool. Mirrors the GET clause-detail
 * endpoint that powers ClauseDetailDrawer, so an in-app agent can
 * read everything the auditor sees: the clause text, current
 * bindings (with provenance), pending LLM proposals waiting for
 * human review, and the live SoA decision.
 *
 * Read-only. No preview/confirm cycle.
 */

import type { IAgentTool, IAgentToolContext, AgentToolResult } from '../../../../core/ai/agent/IAgentTool';

function getFolderId(context: IAgentToolContext): string | null {
  const ctx = context.assistantContext as Record<string, unknown> | undefined;
  const f = ctx?.folderId;
  return typeof f === 'string' && f.length > 0 ? f : null;
}

interface ClauseDetail {
  clause: { clauseId: string; title: string; description: string; family: string | null };
  framework: { name: string; version: string | null };
  effectiveStatus: string;
  bindings: Array<{
    label: string;
    sourceFolderName: string | null;
    via: string;
    edgeSource?: string;
    llmAttribution?: { confidence: number | null; rationale: string | null } | undefined;
  }>;
  pendingProposals: Array<{
    proposalId: string;
    processNodeLabel: string | null;
    sourceFolderName: string | null;
    confidence: number | null;
    rationale: string | null;
  }>;
  soa: { status: string; justification: string } | null;
}

export const reviewClauseTool: IAgentTool = {
  name: 'review_clause',
  description:
    'Read the full resolution detail for one compliance clause in the current Compass folder: clause text, current evidence bindings with their provenance, AI-suggested matches awaiting human approval, and any Statement of Applicability decision in place. Use before deciding on a proposal or setting SoA.',
  parameters: {
    type: 'object',
    properties: {
      frameworkRecordId: {
        type: 'string',
        description: 'UUID of the framework record attached to this folder.',
      },
      clauseRecordId: {
        type: 'string',
        description: 'UUID of the clause record (Control with frameworkRef).',
      },
    },
    required: ['frameworkRecordId', 'clauseRecordId'],
  },
  supportedDiagramTypes: [],
  tier: 'safe',
  category: 'query',

  async execute(params, context): Promise<AgentToolResult> {
    const folderId = getFolderId(context);
    if (!folderId) return { success: false, summary: 'No folder context available.' };
    const frameworkRecordId = String(params.frameworkRecordId ?? '');
    const clauseRecordId = String(params.clauseRecordId ?? '');
    if (!frameworkRecordId || !clauseRecordId) {
      return { success: false, summary: 'frameworkRecordId and clauseRecordId are required.' };
    }

    const url =
      `/api/compass/folders/${encodeURIComponent(folderId)}` +
      `/audit/${encodeURIComponent(frameworkRecordId)}` +
      `/clauses/${encodeURIComponent(clauseRecordId)}/detail`;
    const resp = await fetch(url);
    if (!resp.ok) return { success: false, summary: `Clause detail request failed (${resp.status})` };
    const data = (await resp.json()) as ClauseDetail | { error: string };
    if ('error' in data) return { success: false, summary: data.error };

    const lines: string[] = [];
    lines.push(`${data.framework.name}${data.framework.version ? ` v${data.framework.version}` : ''} · ${data.clause.clauseId} — ${data.clause.title}`);
    lines.push(`Status: ${data.effectiveStatus.replace('_', ' ')}`);
    if (data.clause.description) {
      lines.push('', `Requirement: ${data.clause.description.slice(0, 400)}${data.clause.description.length > 400 ? '…' : ''}`);
    }
    if (data.bindings.length > 0) {
      lines.push('', `Evidence (${data.bindings.length}):`);
      for (const b of data.bindings.slice(0, 6)) {
        const llm = b.llmAttribution
          ? ` [AI ${b.llmAttribution.confidence !== null ? `${Math.round(b.llmAttribution.confidence * 100)}%` : ''}]`
          : '';
        lines.push(`- ${b.label}${b.sourceFolderName ? ` (in ${b.sourceFolderName})` : ''} · via ${b.via}${llm}`);
      }
    }
    if (data.pendingProposals.length > 0) {
      lines.push('', `AI suggestions awaiting review (${data.pendingProposals.length}):`);
      for (const p of data.pendingProposals.slice(0, 6)) {
        const pct = p.confidence !== null ? `${Math.round(p.confidence * 100)}%` : '?%';
        lines.push(`- [${p.proposalId.slice(0, 8)}] ${p.processNodeLabel ?? '?'} (${pct}) — ${p.rationale ?? ''}`);
      }
    }
    if (data.soa) {
      lines.push('', `SoA: ${data.soa.status} — ${data.soa.justification}`);
    }
    if (data.bindings.length === 0 && data.pendingProposals.length === 0 && !data.soa) {
      lines.push('', 'No bindings, no pending proposals, no SoA. Options: tag a process step with the clause id, run the AI matcher, or set an SoA decision.');
    }

    return {
      success: true,
      summary: lines.join('\n'),
      // AgentToolResult.data is typed as Record<string, unknown>; the
      // ClauseDetail interface is structurally compatible but lacks an
      // index signature, so cast through unknown.
      data: data as unknown as Record<string, unknown>,
      uiSurface: 'chat',
    };
  },
};
